import uuid from "uuid";
import SaveEstimateRequest from "./save-estimate-request";
import EstimateGateway from "./estimate-gateway";
import Estimate from "./estimate";
import EstimateItem from "./estimate-item";
import UnitPrice from "@/accounting/domain/models/unit-price";
import { calcTotalLoss } from "@/accounting/domain/models/loss-total";
import { toEstimate } from "@/accounting/application/requests/estimate-request";

export default class EstimateService {
  public constructor(private estimates: EstimateGateway) {}

  public async find(id: number): Promise<Estimate> {
    const e = await this.estimates.find(id);
    if (!e) {
      throw new Error("estimate is not found.");
    }
    return e;
  }

  public async save(estimate: SaveEstimateRequest): Promise<number> {
    return await this.estimates.save(estimate);
  }

  public async changeStatus(id: string, status: string): Promise<void> {
    const estimate = await this.find(Number(id));
    estimate.status = status;
    await this.save(estimate);
  }

  public async addNewItem(estimate: Estimate): Promise<void> {
    const newItem = new EstimateItem();
    newItem.id = uuid();
    estimate.items.push(newItem);
  }

  public async addNewItemAfter(
    estimate: Estimate,
    itemId: string
  ): Promise<void> {
    const [items, i] = this.getItemListAndIndex(estimate, itemId);
    if (i < 0) {
      this.addNewItem(estimate);
    } else {
      const newItem = new EstimateItem();
      newItem.id = uuid();
      items.splice(i + 1, 0, newItem);
    }
  }

  private getItemListAndIndex(
    estimate: Estimate,
    itemId: string
  ): [EstimateItem[], number] {
    const i = estimate.items.findIndex(({ id }) => id === itemId);
    if (i < 0) {
      const [items, j] = estimate.items.reduce(
        (c, it) => {
          const j = it.items.findIndex(({ id }) => id === itemId);
          if (j < 0) return c;
          return [it.items, j];
        },
        [[] as EstimateItem[], -1]
      );
      if (j < 0) return [[], -1];
      return [items, j];
    } else {
      return [estimate.items, i];
    }
  }

  public async removeItem(
    estimate: Estimate,
    itemId: string
  ): Promise<Estimate> {
    estimate.items.forEach(p => {
      const i = p.items.findIndex(item => item.id === itemId);
      if (i < 0) return;
      p.items.splice(i, 1);
    });
    const i = estimate.items.findIndex(item => item.id === itemId);
    if (i < 0) return estimate;
    estimate.items.splice(i, 1);
    return await this.calculateTotal(estimate);
  }

  public async removeAllItems(estimate: Estimate): Promise<Estimate> {
    estimate.items = [];
    return await this.calculateTotal(estimate);
  }

  public async destroy(id: number): Promise<void> {
    await this.estimates.destroy(id);
  }

  public async calculateTotal(params: Estimate): Promise<Estimate> {
    const paymentMethod = (params as any).paymentMethod;
    const estimatePaymentMethods = (params as any).estimatePaymentMethods;
    const estimate = toEstimate(params);
    const json: any = estimate.toJSON();
    json.paymentMethod = paymentMethod;
    json.estimatePaymentMethods = estimatePaymentMethods;
    return json;
  }

  /**
   * 発注額を自動計算する
   */
  public async calculateInvoice(estimate: Estimate): Promise<Estimate> {
    const items = estimate.items.map(i => {
      if (i.items.length > 0) {
        // 小項目がある場合は小項目の合計を大項目の単価にする
        const items = i.items.map(j => {
          const unitLoss = new UnitPrice(j.unitPrice)
            .loss(estimate.lossRate)
            .toNumber();
          return { ...j, unitLoss };
        });
        return { ...i, items };
      } else {
        // 小項目がない場合は入力された単価から計算する
        const unitLoss = new UnitPrice(i.unitPrice)
          .loss(estimate.lossRate)
          .toNumber();
        return { ...i, unitLoss };
      }
    });
    estimate.lossTotal = calcTotalLoss(estimate.lossRate, estimate.subtotal);
    return await this.calculateTotal({ ...estimate, items });
  }

  public async duplicate(id: number): Promise<number> {
    const origin = await this.estimates.find(id);
    if (!origin) {
      return await this.save(new Estimate());
    }
    const estimate = { ...origin, id: 0 };
    // 複製時にアイテムのidが被らないようにする
    this.changeItemIds(estimate.items);
    return await this.save(estimate);
  }

  private changeItemIds(items: any[]) {
    if (items.length === 0) return;
    items.forEach(item => {
      item.id = uuid();
      this.changeItemIds(item.items);
    });
  }
}
